import { endId, type FG, type GLink, type GNode } from './internal.js';

const DURATION_MS = 8000;

/**
 * Creation-order replay of the vault (Obsidian's "animate" feature): real
 * nodes are sorted by ctime and added progressively (with the links whose
 * both endpoints exist; phantom targets pop in with their first source),
 * lightly re-heating the simulation on each batch. Node objects are shared
 * with the live graph, so positions carry through and the final restore is
 * seamless. Arrays grow in place — no per-frame reallocation of graph data.
 */
export class Timelapse {
  private raf = 0;
  private startTime = 0;
  private order: GNode[] = [];
  private byId = new Map<string, GNode>();
  private linksBy = new Map<string, GLink[]>();
  private included = new Set<string>();
  private linkIncluded = new Set<GLink>();
  private tlNodes: GNode[] = [];
  private tlLinks: GLink[] = [];
  private count = 0;
  private active = false;

  constructor(
    private fg: FG,
    private full: { nodes: GNode[]; links: GLink[] },
    private onProgress: (p: number) => void,
    private onDone: () => void,
  ) {}

  /** Returns false (and does nothing) when there is too little to replay. */
  start(): boolean {
    this.order = this.full.nodes
      .filter((n) => n.kind !== 'phantom')
      .sort((a, b) => a.ctime - b.ctime || a.path.localeCompare(b.path));
    if (this.order.length < 2) return false;
    for (const n of this.full.nodes) this.byId.set(n.id, n);
    for (const l of this.full.links) {
      const s = endId(l.source);
      const t = endId(l.target);
      let ls = this.linksBy.get(s);
      if (!ls) this.linksBy.set(s, (ls = []));
      ls.push(l);
      if (t !== s) {
        let lt = this.linksBy.get(t);
        if (!lt) this.linksBy.set(t, (lt = []));
        lt.push(l);
      }
    }
    this.active = true;
    this.startTime = performance.now();
    this.applyProgress(0);
    this.onProgress(0);
    this.raf = requestAnimationFrame(this.step);
    return true;
  }

  private step = (now: number): void => {
    if (!this.active) return;
    const p = Math.min((now - this.startTime) / DURATION_MS, 1);
    this.applyProgress(p);
    this.onProgress(p);
    if (p >= 1) {
      this.finish();
      return;
    }
    this.raf = requestAnimationFrame(this.step);
  };

  private applyProgress(p: number): void {
    const target = Math.max(1, Math.ceil(p * this.order.length));
    if (target === this.count) return;
    if (target < this.count) {
      // Backwards seek: rebuild from the start (rare, cheap at <1k nodes).
      this.included.clear();
      this.linkIncluded.clear();
      this.tlNodes = [];
      this.tlLinks = [];
      this.count = 0;
    }
    for (let i = this.count; i < target; i++) this.addNode(this.order[i]);
    this.count = target;
    this.fg.graphData({ nodes: this.tlNodes, links: this.tlLinks });
    this.fg.d3ReheatSimulation();
  }

  private addNode(n: GNode): void {
    if (this.included.has(n.id)) return;
    this.included.add(n.id);
    this.tlNodes.push(n);
    for (const l of this.linksBy.get(n.id) ?? []) {
      if (this.linkIncluded.has(l)) continue;
      const s = endId(l.source);
      const t = endId(l.target);
      const otherId = s === n.id ? t : s;
      if (!this.included.has(otherId)) {
        const other = this.byId.get(otherId);
        // Phantom targets appear with their first linking source.
        if (!other || other.kind !== 'phantom') continue;
        this.addNode(other);
        if (this.linkIncluded.has(l)) continue; // added by the recursion
      }
      this.linkIncluded.add(l);
      this.tlLinks.push(l);
    }
  }

  /** Scrubber seek; keeps playing from the new position. */
  seek(p: number): void {
    if (!this.active) return;
    const clamped = Math.min(Math.max(p, 0), 1);
    this.startTime = performance.now() - clamped * DURATION_MS;
    this.applyProgress(clamped);
    this.onProgress(clamped);
  }

  /** Cancel: restore the full graph and notify. */
  stop(): void {
    if (!this.active) return;
    this.finish();
  }

  private finish(): void {
    this.active = false;
    cancelAnimationFrame(this.raf);
    this.fg.graphData({ nodes: this.full.nodes, links: this.full.links });
    this.fg.d3ReheatSimulation();
    this.onDone();
  }

  /** Unmount cleanup only — leaves the (about to be destroyed) fg alone. */
  dispose(): void {
    this.active = false;
    cancelAnimationFrame(this.raf);
  }
}
